import React from 'react'
import { css } from '@emotion/css'
import { useIntl } from 'react-intl'
import { getRbacStatus } from 'contrastBase/accessControl'
import { ExpandableList, ListLoading } from 'contrastBase/layout'
import { IconHelp } from '@contrast-security-inc/design-system-foundations'
import { Stack } from '@contrast-security-inc/design-system-layouts'
import { Tooltip } from '@contrast-security-inc/design-system-popovers'
import { Text } from '@contrast-security-inc/design-system-typography'
import { MetaSelect } from './meta-select'
import { useMetadataFields } from '../api'
import {
  getOrgUuid,
  identity,
  identityOrEmptyString,
  sortByProperty
} from '../common/utils'
import { useOrganizationProfile } from '../../../../session-metadata/api'
import { Button } from '@contrast-security-inc/design-system-buttons'
import {
  MetadataField,
  titleIconCss,
  useApplicationsSearch,
  useEacGroupsSearch,
  useRbacGroupsSearch,
  useTagsSearch
} from './agent-config'
import { formatStyles } from '../../../../react-intl-helpers'

export const RegisterApplication = () => {
  return <NormalizedAgentDownloadRegisterAppLayout />
}

const NormalizedAgentDownloadRegisterAppLayout = () => {
  const formElements = [AppDetails, AppMetadata, RegisterButton]
  return (
    <Stack
      testId="normalizedLayout"
      flexDirection="column"
      className={css`
        margin-top: 16px;
      `}
    >
      <Stack flexDirection="row">
        {formElements.map((Element, i) => (
          <Stack
            key={'stack' + i}
            flexDirection="column"
            className={css`
              width: 33.33%;
            `}
          >
            <Element />
          </Stack>
        ))}
      </Stack>
    </Stack>
  )
}

const RegisterButton = () => {
  const intl = useIntl()
  return (
    <Button
      type="submit"
      className={css`
        margin-left: auto;
      `}
    >
      {intl.formatMessage({ id: 'title_registerApplication' })}
    </Button>
  )
}

const AppDetails = () => {
  const intl = useIntl()
  const organizationUuid = getOrgUuid()
  const { isAssessEnforced: isRbacEnforced } = getRbacStatus()
  const { isLoading: isLoadingOrgProfile, data: orgProfile } =
    useOrganizationProfile({ organizationUuid })
  const isGroupsAvailable = !isLoadingOrgProfile
    ? orgProfile?.roles?.includes('ROLE_ADMIN') || isRbacEnforced
    : false
  // prettier-ignore
  return (
    <>
      <Text font="titleH6" className={titleIconCss}>
        {intl.formatMessage({ id: 'title_applicationDetails' })}
        <Tooltip
          body={intl.formatMessage({
            id: 'tooltip_agentConfigApplicationDetails'
          })}
        >
          <IconHelp />
        </Tooltip>
      </Text>
      <MetaSelect
        name="name"
        onCreateItem={(term) => term}
        testId="agent-config-select-application-name"
        itemToID={identity}
        itemToString={identityOrEmptyString}
        useSearch={useApplicationsSearch}
        label={intl.formatMessage({ id: 'title_applicationName' })}
      />
      {isGroupsAvailable && (
        <MetaSelect
          name="appGroups"
          isAsync={false}
          isMulti={true}
          testId="agent-config-select-application-groups"
          itemToID={identity}
          itemToString={identityOrEmptyString}
          useSearch={
            isRbacEnforced ? useRbacGroupsSearch : useEacGroupsSearch
          }
          label={intl.formatMessage({
            id: isRbacEnforced
              ? 'title_resourceGroups'
              : 'title_applicationGroups'
          })}
          renderGuidance={
            isRbacEnforced
              ? (items) => {
                return (
                  <span
                    className={css`
                        display: block;
                      `}
                  >
                    {intl.formatMessage(
                      {
                        id:
                          items.length === 1
                            ? 'detail_onboard_accessToOneResourceGroup'
                            : 'detail_onboard_accessToMultipleResourceGroups'
                      },
                      {
                        ...formatStyles.doclink({
                          path: 'resource-groups.html'
                        })
                      }
                    )}
                  </span>
                ) as unknown as string
              }
              : undefined
          }
        />
      )}
      <MetaSelect
        name="appTags"
        isMulti={true}
        onCreateItem={(term) => term}
        testId="agent-config-select-application-tags"
        itemToID={identity}
        itemToString={identityOrEmptyString}
        useSearch={useTagsSearch}
        label={intl.formatMessage({ id: 'title_tags_application' })}
      />
    </>
  )
}

const AppMetadata = () => {
  const intl = useIntl()
  const organizationUuid = getOrgUuid()
  const { isLoading: isLoadingMetadataFields, data } = useMetadataFields({
    organizationUuid
  })
  const metadataFields =
    data?.fields?.sort(sortByProperty('required')).reduce(
      (acc, it) =>
        it.subfields // flatten subfields
          ? [...acc, ...it.subfields]
          : [...acc, it],
      []
    ) || []
  const metadataFieldsCount = // display all required fields or first 2
    metadataFields.reduce((acc, it) => acc + (it.required ? 1 : 0), 0) || 2

  if (isLoadingMetadataFields) {
    return <ListLoading />
  }

  if (metadataFields.length === 0) {
    return null
  }
  return (
    <>
      <Text font="titleH6" className={titleIconCss}>
        {intl.formatMessage({
          id: 'title_customMetadata_customFields'
        })}
        <Tooltip
          body={intl.formatMessage({
            id: 'tooltip_agentConfigApplicationMetadata'
          })}
        >
          <IconHelp />
        </Tooltip>
      </Text>
      <ExpandableList
        defaultItemsDisplayed={metadataFieldsCount}
        listItems={metadataFields}
        ItemComponent={MetadataField}
      />
    </>
  )
}
